import { ArrowDropUpOutlined, FilterListOutlined } from "@mui/icons-material";
import {
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Box,
    Collapse,
    Stack,
    Button,
} from "@mui/material";
import { BarSeriesType } from "@mui/x-charts";
import { BarChart } from "@mui/x-charts/BarChart";
import { MakeOptional } from "@mui/x-charts/internals";
import { PieChart } from "@mui/x-charts/PieChart";
import React, { useCallback, useMemo, useState } from "react";

import { theme } from "../../../../../assets";
import Tag from "../../../../../components/atoms/tag/tag";
import { TProject } from "../../../../../models";
import FilterPopover from "./filter-popover";

const colors = [
    "#0B49EC",
    theme.pink1,
    theme.orange,
    "#9D0000",
    "#03BA78",
    theme.green1,
    "#0CB8B8",
    "#FF015C",
    "#9933FF",
    "#824700",
    "#800380",
    "#4B07BF",
    "#D14141",
    "#8BB908",
] as const;
interface IEntry {
    Activity: {
        ActivitySection: { name: string };
        authorName: string;
        id_e: string;
        name: string;
    } | null;
    total_time: number;
    user_id: string;
}

interface IDashboardProps {
    barChartContent: {
        barChartData: string[];
        barChartSeries: MakeOptional<BarSeriesType, "type">[];
    };
    entries: IEntry[];
    handleFilterUserById: (value?: string[]) => void;
    handleClearFilterUserById: () => void;
    time_requested: { [key: string]: number } | [];
    project: TProject;
    selectedUsers: string[];
}

const b2Style = {
    color: "rgba(62, 55, 78, 0.87)",
    fontFamily: "Noto Sans",
    fontSize: "0.875rem",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "1.25rem ",
    letterSpacing: "0.0088rem",
};

const h5Style = {
    color: "rgba(62, 55, 78, 0.87)",
    fontFamily: "Noto Sans",
    fontSize: "1.25rem",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "1.75rem",
    letterSpacing: "0.0019rem",
};

export const formatMinutesToHours = (totalMinutes: number): string => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const paddedMinutes = String(minutes.toFixed(0)).padStart(2, "0"); // ex: 5 => "05"
    return `${hours}:${paddedMinutes}`;
};

const Dashboard: React.FC<IDashboardProps> = ({
    barChartContent,
    entries,
    handleClearFilterUserById,
    handleFilterUserById,
    selectedUsers,
    project,
    time_requested,
}) => {
    const [openRows, setOpenRows] = useState<{ [key: string]: boolean }>({});
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const toggleRow = (userId: string) => {
        setOpenRows((prev) => ({
            ...prev,
            [userId]: !prev[userId],
        }));
    };

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, [anchorEl, setAnchorEl]);
    const handleOpen = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
        },
        [anchorEl, setAnchorEl]
    );
    const userTimeMap: { [key: string]: number } = {};
    entries.forEach(({ user_id, total_time }) => {
        userTimeMap[user_id] = (userTimeMap[user_id] || 0) + total_time;
    });

    const totalTime = Object.values(userTimeMap).reduce(
        (sum, time) => sum + time,
        0
    );
    const userPercentageMap: { [key: string]: number } = {};
    Object.keys(userTimeMap).forEach((userId) => {
        userPercentageMap[userId] = (userTimeMap[userId] / totalTime) * 100;
    });

    const pieChartData = useMemo(
        () =>
            Object.keys(userTimeMap).map((userId) => {
                return {
                    id: userId,
                    value: Number(userTimeMap[userId]),
                    label:
                        project.Users?.find((user) => user.id_e === userId)
                            ?.name ?? "Not Found",
                    color: colors[
                        Math.floor(userTimeMap[userId] % colors.length)
                    ],
                };
            }),
        [userTimeMap]
    );

    const usersOptions =
        project.Users?.map((user) => ({
            label: user.name,
            value: user.id_e,
        })) ?? [];
    const havePopoverFiltersApplied = selectedUsers.length > 0;

    return (
        <Stack
            sx={{
                gap: "2.5rem",
                padding: "1.25rem",
                height: "100vh",
            }}
        >
            <Box sx={{ flex: 1 }}>
                <Stack
                    sx={{
                        gap: "1.5rem",
                    }}
                >
                    <Typography variant="h5" sx={h5Style}>
                        Horas Realizadas
                    </Typography>
                    <BarChart
                        xAxis={[
                            {
                                scaleType: "band",
                                data: barChartContent.barChartData,
                            },
                        ]}
                        yAxis={[
                            {
                                valueFormatter: (value) =>
                                    `${(value / 60).toFixed(0)}h`,
                            },
                        ]}
                        series={barChartContent.barChartSeries}
                        height={400}
                    />
                </Stack>
            </Box>
            <Box
                sx={{
                    display: "flex",
                    gap: "2.5rem",
                    width: "100%",
                    flex: 1,
                }}
            >
                <Stack
                    sx={{
                        display: "flex",
                        width: "66%",

                        gap: "1.5rem",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                        }}
                    >
                        <Typography variant="h5">
                            Colaboradores e Atividades
                        </Typography>
                        <Button
                            variant="text"
                            sx={{
                                gap: "0.5rem",
                                color: havePopoverFiltersApplied
                                    ? "#B93BDC"
                                    : theme.primary.main,
                            }}
                            onClick={handleOpen}
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                }}
                            >
                                <FilterListOutlined />
                                {havePopoverFiltersApplied && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: "2px",
                                            right: "0px",
                                            width: "0.5rem",
                                            height: "0.5rem",
                                            borderRadius: "50%",
                                            backgroundColor: "#EF6C00",
                                        }}
                                    />
                                )}
                            </Box>
                            FILTROS
                        </Button>
                        {anchorEl && (
                            <FilterPopover
                                anchorEl={anchorEl}
                                selectedUsers={selectedUsers}
                                users={usersOptions}
                                handleClose={handleClose}
                                handleSetPopoverData={handleFilterUserById}
                            />
                        )}
                        {havePopoverFiltersApplied && (
                            <Button
                                variant="text"
                                sx={{
                                    gap: "0.5rem",
                                    color: "#EF6C00",
                                }}
                                onClick={handleClearFilterUserById}
                            >
                                LIMPAR FILTROS
                            </Button>
                        )}
                    </Box>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell />

                                <TableCell
                                    align="left"
                                    sx={{
                                        width: "80%",
                                    }}
                                >
                                    Título
                                </TableCell>
                                <TableCell align="right">Tempo</TableCell>
                                <TableCell align="right">Porcentagem</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.keys(userTimeMap).map((userId, index) => {
                                const user = project.Users?.find(
                                    (u) => u.id_e === userId
                                );
                                const userEntries = entries.filter(
                                    (entry) => entry.user_id === userId
                                );

                                const color =
                                    colors[
                                        Math.floor(
                                            userTimeMap[userId] % colors.length
                                        )
                                    ];

                                return (
                                    <React.Fragment key={userId}>
                                        {/* Linha principal */}
                                        <TableRow
                                            onClick={() => toggleRow(userId)}
                                            sx={{
                                                cursor: "pointer",
                                                backgroundColor: openRows[
                                                    userId
                                                ]
                                                    ? theme.gray100
                                                    : "inherit",
                                                ":hover": {
                                                    backgroundColor:
                                                        theme.gray100,
                                                },
                                            }}
                                        >
                                            <TableCell>
                                                <Button
                                                    variant="outlined"
                                                    sx={{
                                                        width: "3.625rem",
                                                        height: "2rem",
                                                        border: "1px solid #888888",
                                                        color: "#888888",
                                                        borderRadius: "0.25rem",
                                                        ":hover": {
                                                            backgroundColor:
                                                                theme.gray100,
                                                        },
                                                    }}
                                                >
                                                    <ArrowDropUpOutlined
                                                        sx={{
                                                            rotate: openRows[
                                                                userId
                                                            ]
                                                                ? "180deg"
                                                                : "0deg",
                                                        }}
                                                    />{" "}
                                                    {userEntries.length}
                                                </Button>
                                            </TableCell>
                                            <TableCell
                                                align="left"
                                                sx={{
                                                    width: "80%",
                                                }}
                                            >
                                                <Tag
                                                    isFavorite={false}
                                                    color={color}
                                                    maxWidth="11rem"
                                                >
                                                    {user?.name ?? "Not found"}
                                                </Tag>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    variant="body2"
                                                    sx={b2Style}
                                                >
                                                    {formatMinutesToHours(
                                                        userTimeMap[userId]
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    variant="body2"
                                                    sx={b2Style}
                                                >
                                                    {totalTime > 0
                                                        ? `${userPercentageMap[
                                                              userId
                                                          ].toFixed(2)}%`
                                                        : "-"}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>

                                        {/* Linha colapsável, corrigida */}
                                        <TableRow
                                            sx={{
                                                borderBottom: "unset",
                                                backgroundColor: theme.gray100,
                                            }}
                                        >
                                            <TableCell
                                                colSpan={4}
                                                style={{ padding: 0 }}
                                            >
                                                <Collapse in={openRows[userId]}>
                                                    <Table>
                                                        <TableBody>
                                                            {userEntries.map(
                                                                (entry, i) => {
                                                                    const isLastIndex =
                                                                        i +
                                                                            1 ===
                                                                        userEntries.length;
                                                                    const entryPercentage =
                                                                        (entry.total_time /
                                                                            userTimeMap[
                                                                                userId
                                                                            ]) *
                                                                        100;
                                                                    return (
                                                                        <TableRow
                                                                            key={
                                                                                entry
                                                                                    .Activity
                                                                                    ?.id_e
                                                                            }
                                                                            sx={{
                                                                                borderBottom:
                                                                                    isLastIndex
                                                                                        ? "unset"
                                                                                        : `1px solid ${theme.gray200}`,
                                                                            }}
                                                                        >
                                                                            <TableCell
                                                                                align="left"
                                                                                sx={{
                                                                                    width: "80%",
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={
                                                                                        b2Style
                                                                                    }
                                                                                >
                                                                                    {entry
                                                                                        .Activity
                                                                                        ?.name ??
                                                                                        "Not found"}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell align="right">
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={
                                                                                        b2Style
                                                                                    }
                                                                                >
                                                                                    {formatMinutesToHours(
                                                                                        entry.total_time
                                                                                    )}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell align="right">
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={
                                                                                        b2Style
                                                                                    }
                                                                                >
                                                                                    {userTimeMap[
                                                                                        userId
                                                                                    ] >
                                                                                    0
                                                                                        ? `${entryPercentage.toFixed(
                                                                                              2
                                                                                          )}%`
                                                                                        : "-"}
                                                                                </Typography>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                }
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Stack>
                <Stack
                    sx={{
                        width: "33%",
                        justifyContent: "center",
                        alignContent: "center",
                        alignItems: "center",

                        gap: "1.5rem",
                    }}
                >
                    <Typography variant="h5" sx={h5Style}>
                        Horas por Colaboradores
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            gap: "1rem",
                        }}
                    >
                        <PieChart
                            series={[
                                {
                                    data: pieChartData.map((item) => ({
                                        ...item,
                                        color: item.color, // Aqui, estamos pegando a cor do objeto
                                    })),
                                    valueFormatter: (value) =>
                                        formatMinutesToHours(value.value),
                                },
                            ]}
                            slotProps={{
                                legend: { itemMarkWidth: 0, hidden: true },
                            }}
                            height={200}
                        />
                        <Stack>
                            {pieChartData.map((item) => (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    key={item.id}
                                    mb={1}
                                >
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            backgroundColor: item.color,
                                            marginRight: 1,
                                        }}
                                    />
                                    <Typography variant="body2">
                                        {item.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </Stack>
            </Box>
        </Stack>
    );
};

export default Dashboard;
